import { AssetType, JobType, type JobPriorityType } from '@/enum'
import type {
  IJobAssociatedAssetInfo,
  IJobAssociatedAssetUpdate,
  IJobAssociatedMaintenanceTypeUpdate,
  IJobUpdate,
  IJobScheduleOptions,
  IJobForm
} from '@/interfaces'
import type { IDocument } from '@/interfaces/IDocument'
import type { IJobEmployeeUpdate } from '@/interfaces/jobs/IJobEmployeeUpdate'
import type { IJobTaskTemplateUpdate } from '@/interfaces/jobs/IJobTaskTemplateUpdate'
import DocumentModel from '../DocumentModel'
import JobAssociatedAssetInfo from './JobAssociatedAssetInfo'
import JobAssociatedMaintenanceTypeUpdate from './JobAssociatedMaintenanceTypeUpdate'
import { isDefined, toUTCDate } from '@/utils/helpers'
import JobScheduleOptions from './JobScheduleOptions'
import JobAssignedEmployeeUpdate from './JobAssignedEmployeeUpdate'
import { JobTaskTemplateUpdate } from '../task-templates'

class JobUpdate implements IJobUpdate {
  title: string

  description?: string | null

  type?: JobType

  priority?: JobPriorityType | null

  options?: IJobScheduleOptions

  dueDate?: string | null

  employees?: IJobEmployeeUpdate[]

  asset?: IJobAssociatedAssetUpdate

  assetInfo?: IJobAssociatedAssetInfo

  issueReporterId?: string

  workOrderVendorId?: string

  workOrderUrl?: string

  maintenanceTypes: IJobAssociatedMaintenanceTypeUpdate[]

  taskTemplates?: IJobTaskTemplateUpdate[]

  documents?: IDocument[]

  affectCompletedTasks?: boolean

  enforceSequentialTaskCompletion?: boolean

  targetOdometerReading?: number | null

  targetMachineHours?: number | null

  constructor(jobForm?: IJobForm) {
    this.title = jobForm?.title ?? ''
    this.description = jobForm?.description
    this.type = jobForm?.type ?? JobType.GENERAL
    this.priority = jobForm?.priority

    this.dueDate = jobForm?.dueDate ? toUTCDate(jobForm?.dueDate) : null
    this.options = this.isRecurring(jobForm?.options)
      ? new JobScheduleOptions(jobForm?.options)
      : undefined

    if (jobForm?.asset?.id && jobForm.asset.type !== undefined && jobForm.asset.type !== null) {
      this.asset = {
        id: jobForm.asset.id,
        type: jobForm.asset.type
      } as IJobAssociatedAssetUpdate
    }

    if (jobForm?.asset?.id) {
      if (jobForm?.asset?.type === AssetType.VEHICLE) {
        this.assetInfo = new JobAssociatedAssetInfo({
          machineHours: jobForm?.asset?.vehicle?.machineHours,
          odometerReading: jobForm?.asset?.vehicle?.odometerReading
        })
      }

      if (jobForm?.asset?.type === AssetType.EQUIPMENT) {
        this.assetInfo = new JobAssociatedAssetInfo({
          machineHours: jobForm?.asset?.equipment?.machineHours
        })
      }

      if (jobForm?.asset?.type === AssetType.MACHINE) {
        this.assetInfo = new JobAssociatedAssetInfo({
          machineHours: jobForm?.asset?.machine?.machineHours,
          odometerReading: jobForm?.asset?.machine?.odometerReading
        })
      }
    }

    if (Array.isArray(jobForm?.assignedEmployeeIds)) {
      this.employees = jobForm?.assignedEmployeeIds?.map((id: string, idx: number) => {
        return new JobAssignedEmployeeUpdate({
          id: id,
          order: jobForm?.assignedEmployeeIds?.length == 1 ? -1 : idx + 1
        })
      })
    } else {
      this.employees = [
        new JobAssignedEmployeeUpdate({
          id: jobForm?.assignedEmployeeIds,
          order: -1
        })
      ]
    }

    // if (jobForm?.type == JobType.MILL_ORDER) {
    //   this.employees = []

    //   if (jobForm?.firstEmployee) {
    //     this.employees.push({
    //       id: jobForm.firstEmployee,
    //       order: 1
    //     })
    //   }

    //   if (jobForm?.secondEmployee) {
    //     this.employees.push({
    //       id: jobForm.secondEmployee,
    //       order: 2
    //     })
    //   }
    // }

    this.issueReporterId = jobForm?.issueReporterId
    this.workOrderVendorId = jobForm?.workOrderVendorId
    this.workOrderUrl = jobForm?.workOrderUrl

    this.maintenanceTypes =
      jobForm?.maintenanceTypes?.map(
        (maintenanceType: any) => new JobAssociatedMaintenanceTypeUpdate(maintenanceType)
      ) ?? []

    this.taskTemplates =
      jobForm?.taskTemplates?.map((taskTemplate) => new JobTaskTemplateUpdate(taskTemplate)) ?? []

    this.documents = jobForm?.documents?.map((document: any) => new DocumentModel(document))
    this.affectCompletedTasks = jobForm?.affectCompletedTasks
    this.enforceSequentialTaskCompletion = jobForm?.enforceSequentialTaskCompletion
    this.targetOdometerReading = jobForm?.targetOdometerReading ?? null
    this.targetMachineHours = jobForm?.targetMachineHours ?? null
  }

  private isRecurring(options?: IJobScheduleOptions): boolean {
    return (
      isDefined(options?.timeBasedTrigger?.pattern) ||
      isDefined(options?.odometerTrigger?.value) ||
      isDefined(options?.machineHoursTrigger?.value)
    )
  }
}

export default JobUpdate
